import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { User } from '../../model/user';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { DataService } from './../../../core/service/base/data.service';
import { MessageService } from './../../../core/service/message.service';
import { ProgressBarService } from './../../../core/service/progress-bar.service';
import { Observable, of } from 'rxjs';
import { tap, catchError, map, finalize } from 'rxjs/operators';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-selection-item';
import { PagedFilter } from '../../../core/model/paged-filter';

export type UserFilterModel = {
  name: string;
  email: string;
  selectedFields: string[];
  defaultFields: string[];
};

@Injectable()
export class UserService extends DataService<User> {
  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, messageService, User, progressBar, 'user');
  }

  getFilterDefinition(): Observable<PagedFilter<UserFilterModel>> {
    return this.client
      .get<any>(`${this.endpoint}/filter`)
      .pipe(
        map((res: PagedFilter<UserFilterModel>) => {
          const result: PagedFilter<UserFilterModel> = {
            page: res.page,
            pageSize: res.pageSize,
            descending: res.descending,
            filter: {
              name: res.filter?.name,
              email: res.filter?.email,
              selectedFields: Array.isArray(res.filter?.selectedFields) ? res.filter.selectedFields : ['personName', 'email'],
              defaultFields: ['personName', 'email'],
            },
          };
          if (res.orderBy) {
            result.orderBy = res.orderBy;
          }
          return result;
        })
      )
      .pipe(
        catchError(error => {
          console.log(error);
          return [];
        })
      );
  }

  getSelection(filter: string, options?: any, page?: number): Observable<AutoCompleteSelectionItem[]> {
    let queryArgs = {
      filter: filter,
      maxItems: 10,
    };
    if (options) queryArgs['options'] = options;
    if (page && page > 0) queryArgs['page'] = page;

    return this.client.post<User[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => this.createAutoCompleteSelectionItem(item))),
      catchError(e => {
        this.messageService.showError(e);
        return [];
      })
    );
  }

  private createAutoCompleteSelectionItem(contact: User): AutoCompleteSelectionItem {
    if (!contact) {
      return new AutoCompleteSelectionItem('', null, null);
    }
    let shortName = contact.name.trim();
    return new AutoCompleteSelectionItem(contact.name, contact.id, Object.assign(new User(), contact), shortName);
  }
}
